import { Fragment, useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
    Col,
    Row,
    Card,
    CardBody,
    CardHeader,
    Button,
    Alert,
    Spinner,
    Container
} from 'reactstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import { useSelector } from 'react-redux'
import { API_URL } from './utils/Constants/URLS'
import PageHeader from './utils/PageHeader'
import FocStagesAccordion from './FocStagesAccordion'

const FocLeadJourney = () => {
    const { id } = useParams()
    const navigate = useNavigate()
    const userDetails = useSelector(store => store.user)

    // Lead details state
    const [focLeadDetails, setFocLeadDetails] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')
    const [saveAllLoading, setSaveAllLoading] = useState(false)

    // Accordion state - open accordion based on current progress
    const [openAccordion, setOpenAccordion] = useState('1')

    // Refs for Save All functionality (except Sales Order)
    const productRequirementRef = useRef(null)
    const enquiryDetailsRef = useRef(null)
    const marketingPlatformRef = useRef(null)
    const workingPlatformRef = useRef(null)
    const buyingPlatformRef = useRef(null)

    // Save All refs object to pass to FocStagesAccordion
    const saveAllRefs = {
        productRequirementRef,
        enquiryDetailsRef,
        marketingPlatformRef,
        workingPlatformRef,
        buyingPlatformRef
    }

    // Handle Save All - saves all stages except Sales Order
    const handleSaveAll = async () => {
        setSaveAllLoading(true)
        setError('')
        setSuccess('')

        const results = []
        const stages = [
            { ref: productRequirementRef, name: 'Product Requirement' },
            { ref: enquiryDetailsRef, name: 'Enquiry Details' },
            { ref: marketingPlatformRef, name: 'Marketing Platform' },
            { ref: workingPlatformRef, name: 'Working Platform' },
            { ref: buyingPlatformRef, name: 'Buying Platform' }
        ]

        for (const stage of stages) {
            if (stage.ref.current?.saveData) {
                try {
                    const result = await stage.ref.current.saveData()
                    results.push({ ...result, name: stage.name })
                } catch (err) {
                    console.error(`Error saving ${stage.name}:`, err)
                    results.push({ success: false, name: stage.name, error: err })
                }
            }
        }

        const failures = results.filter(r => !r.success && !r.skipped)
        const successes = results.filter(r => r.success)

        if (failures.length > 0) {
            setError(`Failed to save: ${failures.map(f => f.name).join(', ')}`)
        } else {
            const savedStages = successes.filter(s => !s.skipped).map(s => s.name)
            if (savedStages.length > 0) {
                setSuccess(`Successfully saved: ${savedStages.join(', ')}`)
            } else {
                setSuccess('All stages are already completed!')
            }
            // Refresh data after successful save
            fetchFocLeadDetails()
        }

        setSaveAllLoading(false)
        setTimeout(() => {
            setSuccess('')
            setError('')
        }, 5000)
    }

    // Fetch FOC Lead Details
    const fetchFocLeadDetails = async () => {
        setLoading(true)
        setError('')

        try {
            const response = await fetch(`${API_URL}foc/lead/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${userDetails.token}`
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch FOC lead details')
            }

            const result = await response.json()
            setFocLeadDetails(result.data || result)

            // Set active accordion based on lead status
            updateAccordionState(result.data || result)
        } catch (error) {
            console.error('Error fetching FOC lead:', error)
            setError('Failed to load FOC lead details. Please try again.')
        } finally {
            setLoading(false)
        }
    }

    // Update accordion state based on lead progress
    const updateAccordionState = (leadData) => {
        if (!leadData) return

        // Priority order: Product Requirement -> Enquiry -> Marketing -> Working -> Buying -> Sales Order
        if (leadData.productRequirements?.status === 'pending') {
            setOpenAccordion('2') // Product Requirement
        } else if (leadData.enquiryDetails?.status === 'pending') {
            setOpenAccordion('3') // Enquiry Details
        } else if (leadData.marketingPlatform?.status === 'pending') {
            setOpenAccordion('4') // Marketing Platform
        } else if (leadData.workingPlatform?.status === 'pending') {
            setOpenAccordion('5') // Working Platform
        } else if (leadData.buyingPlatform?.status === 'pending') {
            setOpenAccordion('6') // Buying Platform
        } else if (
            leadData.productRequirements?.status !== 'pending' &&
            leadData.enquiryDetails?.status !== 'pending' &&
            leadData.marketingPlatform?.status !== 'pending' &&
            leadData.workingPlatform?.status !== 'pending' &&
            leadData.buyingPlatform?.status !== 'pending'
        ) {
            setOpenAccordion('7') // Sales Order / Agreement
        } else {
            setOpenAccordion('1') // Line Items (default)
        }
    }

    useEffect(() => {
        if (id) {
            fetchFocLeadDetails()
        }
    }, [id])

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading FOC Lead Journey...</p>
            </div>
        )
    }

    if (error && !focLeadDetails) {
        return (
            <Container>
                <Alert color="danger">{error}</Alert>
                <Button color="primary" onClick={() => navigate('/foc')}>
                    Back to FOC Leads
                </Button>
            </Container>
        )
    }

    return (
        <Fragment>
            <div>
                <PageHeader
                    pageName='FOC LEAD JOURNEY'
                    breadcrump1='Home'
                    breadcrump2='FOC Lead Journey'
                    link='/foc'
                />
                <Row className='ms-4 p-4'>
                    <Col xl={12}>
                        {/* Alert Messages */}
                        {error && (
                            <Alert color="danger" toggle={() => setError('')}>
                                {error}
                            </Alert>
                        )}

                        {success && (
                            <Alert color="success" toggle={() => setSuccess('')}>
                                {success}
                            </Alert>
                        )}

                        <Card>
                            <CardHeader className='d-flex justify-content-between align-items-center'>
                                <div className='col-md-6'>
                                    <h5 className='card-title mb-0'>
                                        {focLeadDetails?.leadJourneyName || 'FOC Lead'}
                                    </h5>
                                    <small className="text-muted">
                                        Lead ID: {focLeadDetails?.leadId || 'N/A'}
                                    </small>
                                </div>
                                <div className='col-md-6 text-end'>
                                    <h5 className='text-md mb-2'>{userDetails?.name}</h5>
                                    <div className="d-flex gap-2 justify-content-end">
                                       
                                        <Button
                                            color="secondary"
                                            size="sm"
                                            onClick={() => navigate('/foc')}
                                        >
                                            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                                            Back to FOC Leads
                                        </Button>
                                    </div>
                                </div>
                            </CardHeader>
                            <CardBody>
                                {/* Accordion Component */}
                                <FocStagesAccordion
                                    focLeadDetails={focLeadDetails}
                                    leadId={id}
                                    openAccordion={openAccordion}
                                    setOpenAccordion={setOpenAccordion}
                                    onDataUpdate={fetchFocLeadDetails}
                                    saveAllRefs={saveAllRefs}
                                />
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    )
}

export default FocLeadJourney
